import {
  JobLineItem,
  JobLineItemBase,
  JobLineItemTotals,
} from '@payaca/types/jobContentTypes';
import { TaxRate } from '@payaca/types/taxRateTypes';
import { roundHalfAwayFromZero } from '@payaca/utilities/mathUtilities';

/**
 * The seven values the Line Item totals calculation produces, named to
 * match the persisted `job_line_items.*Value` columns so test assertions
 * against fixture data can compare directly without field-name mapping.
 *
 * All amounts are minor currency units (pennies).
 */
export interface JobLineItemCalculatedTotals {
  subtotalValue: number;
  markupValue: number;
  discountValue: number;
  subtotalIncMarkupDiscountValue: number;
  taxValue: number;
  cisValue: number;
  totalValue: number;
}

/**
 * Canonical Line Item totals calculation. Mirrors the SQL CTE in
 * `apps/server-backend/src/logic/job-line-items/calculated-totals.ts`
 * step-for-step so persisted JS-driven values stay bit-identical to the
 * legacy SQL pipeline.
 *
 * Inputs are the line's stored fields plus its resolved tax rate (or
 * `null` if the line has none — tax then evaluates to 0). All amounts
 * are minor currency units (pennies).
 *
 * Rounding semantics that differ from JavaScript defaults:
 * - Subtotal, tax, and CIS use {@link roundHalfAwayFromZero} (matching
 *   Postgres `round()`), not `Math.round` — the two disagree on negative
 *   half-values.
 * - Markup and discount use `Math.ceil` when the line's subtotal is
 *   non-negative and `Math.floor` when it is negative. Both sign-decisions
 *   are gated on the subtotal, not on the intermediate value being
 *   rounded.
 */
export type CalculateJobLineItemTotalsInput = Pick<
  JobLineItemBase,
  | 'calculatedPrice'
  | 'quantity'
  | 'markupPercentage'
  | 'discountPercentage'
  | 'cisDeductionRate'
> & {
  taxRate: Pick<
    TaxRate,
    'percentage' | 'isIncluded' | 'isReverseCharge'
  > | null;
};

export const calculateJobLineItemTotals = (
  input: CalculateJobLineItemTotalsInput
): JobLineItemCalculatedTotals => {
  const subtotalValue = roundHalfAwayFromZero(
    input.calculatedPrice * input.quantity
  );

  // SQL: ceil() for non-negative subtotals, floor() for negative ones.
  // The wrapper also normalises the negative-zero JS produces from
  // `Math.floor(-0)` (reached when a percentage is null/0 and the
  // subtotal is negative): -0 has no analogue in Postgres `numeric`.
  const roundTowardsMagnitude = (n: number): number => {
    const rounded = subtotalValue >= 0 ? Math.ceil(n) : Math.floor(n);
    return rounded === 0 ? 0 : rounded;
  };

  const markupValue = roundTowardsMagnitude(
    (subtotalValue * (input.markupPercentage ?? 0)) / 100
  );

  const discountValue = roundTowardsMagnitude(
    ((subtotalValue + markupValue) * (input.discountPercentage ?? 0)) / 100
  );

  const subtotalIncMarkupDiscountValue =
    subtotalValue + markupValue - discountValue;

  const taxValue =
    input.taxRate !== null &&
    input.taxRate.isIncluded &&
    !input.taxRate.isReverseCharge
      ? roundHalfAwayFromZero(
          (subtotalIncMarkupDiscountValue * input.taxRate.percentage) / 100
        )
      : 0;

  const cisValue = roundHalfAwayFromZero(
    (subtotalIncMarkupDiscountValue * (input.cisDeductionRate ?? 0)) / 100
  );

  const totalValue = subtotalIncMarkupDiscountValue + taxValue - cisValue;

  return {
    subtotalValue,
    markupValue,
    discountValue,
    subtotalIncMarkupDiscountValue,
    taxValue,
    cisValue,
    totalValue,
  };
};

// This calculation is only valid for dealsV2 where we can assume there will never be any markup or discount on the jobContent, and discount is handles on a line-by-line basis
export const getJoblineItemTotalsFromJobLineItemBase = (
  jobLineItem: Pick<
    JobLineItemBase,
    'calculatedPrice' | 'discountPercentage' | 'markupPercentage'
  >
): JobLineItemTotals => {
  const price = jobLineItem.calculatedPrice;

  // Markup is calculated and applied before discount - markup and discount compound.
  let priceMarkupAmount = 0;

  const roundMethod = price >= 0 ? Math.ceil : Math.floor;

  if (jobLineItem.markupPercentage) {
    priceMarkupAmount = roundMethod(
      price * (jobLineItem.markupPercentage / 100)
    );
  }

  const priceIncMarkup = price + priceMarkupAmount;

  let priceDiscountAmount = 0;

  if (jobLineItem.discountPercentage) {
    priceDiscountAmount = roundMethod(
      priceIncMarkup * (jobLineItem.discountPercentage / 100)
    );
  }

  return {
    priceIncMarkup: priceIncMarkup,
    priceIncMarkupDiscount: priceIncMarkup - priceDiscountAmount,
  };
};

// This calculation is only valid for dealsV2 where we can assume there will never be any markup or discount on the jobContent, and discount is handles on a line-by-line basis
export const getUnitJoblineItemTotalsFromJobLineItem = (
  jobLineItem: Pick<
    JobLineItemBase,
    | 'calculatedPrice'
    | 'quantity'
    | 'discountPercentage'
    | 'vatIncluded'
    | 'isReverseChargeVat'
    | 'vatAmount'
    | 'cisDeductionRate'
    | 'markupPercentage'
  >
): Pick<
  JobLineItem,
  | 'vatTotal'
  | 'cisTotal'
  | 'discountAmount'
  | 'markupAmount'
  | 'subtotalIncMarkupDiscount'
  | 'total'
> => {
  const price = jobLineItem.calculatedPrice;

  // Markup is calculated and applied before discount - markup and discount compound.
  let markupAmount = 0;
  const roundMethod = price >= 0 ? Math.ceil : Math.floor;

  if (jobLineItem.markupPercentage) {
    markupAmount = roundMethod(price * (jobLineItem.markupPercentage / 100));
  }

  const priceIncMarkup = price + markupAmount;

  let discountAmount = 0;

  if (jobLineItem.discountPercentage) {
    discountAmount = roundMethod(
      priceIncMarkup * (jobLineItem.discountPercentage / 100)
    );
  }

  const subtotalIncMarkupDiscount = priceIncMarkup - discountAmount;

  const vatTotal = Math.round(
    jobLineItem.vatIncluded && !jobLineItem.isReverseChargeVat
      ? subtotalIncMarkupDiscount * (jobLineItem.vatAmount / 100)
      : 0
  );

  let cisTotal = undefined;
  if (jobLineItem.cisDeductionRate !== null) {
    cisTotal = Math.round(
      subtotalIncMarkupDiscount * (jobLineItem.cisDeductionRate / 100)
    );
  }

  return {
    markupAmount: markupAmount,
    discountAmount: discountAmount,
    subtotalIncMarkupDiscount: subtotalIncMarkupDiscount,
    vatTotal: vatTotal,
    cisTotal: cisTotal,
    total: subtotalIncMarkupDiscount + vatTotal - (cisTotal || 0),
  };
};

export const getSortedRequiredMultipleOptionalItems = (
  items: JobLineItemBase[]
) => {
  return items.reduce(
    (acc: any, curr: any) => {
      if (curr.isMultipleChoice) acc.multiple.push(curr);
      else if (curr.isOptional) acc.optional.push(curr);
      else acc.required.push(curr);
      return acc;
    },
    { required: [], multiple: [], optional: [] }
  );
};
